/* screens/sales/planForm.jsx — общие части редакторов тарифа: «Закрытые» и «Публичные».

   Вынесено из PrivatePlansScreen, а не скопировано: подписи фич, правила целых полей
   и проверка цены обязаны быть одинаковыми в обоих редакторах. Два списка разошлись
   бы при первой же правке — и одно и то же поле в одной вкладке принимало бы «-1»,
   а в соседней нет. */
import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Field, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Switch } from '../../components/ui/Switch.jsx';
import { fmt } from '../../lib/format.js';

const { useState } = React;

// Фичи — свободный словарь на бэке, но два ключа уже что-то гейтят в проде.
// Держим их подписи здесь, чтобы менеджер видел смысл флага, а не только слаг.
export const FEATURE_LABEL = {
  team_members: 'Команда в пространстве',
  iiko_integration: 'Касса iiko/Syrve и аналитика по ней',
};
export const KNOWN_FEATURES = Object.keys(FEATURE_LABEL);

// Зеркалят SLUG_RE консоли (D9a): ловим очевидное до раунд-трипа, последнее
// слово всё равно за сервером.
export const SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,63}$/;
export const FEATURE_KEY_RE = /^[a-z][a-z0-9_]*$/;

// Целочисленные поля тарифа: у периода и размера файла «безлимита» (-1) нет —
// это счётчики, а не квоты (та же развилка min_value в _apply_plan_payload).
// Период отдельно от лимитов: у публичного тарифа с подписчиками он заморожен,
// а лимиты правятся сразу для всех.
export const PERIOD_FIELD = {
  key: 'period_days', label: 'Период, дней', unit: 'дн.', min: 1, max: 3660,
  hint: 'Сколько дней действует одна оплата.',
};
export const LIMIT_FIELDS = [
  { key: 'chat_messages_per_30d', label: 'Запросов к ассистенту за период', unit: '', min: -1 },
  { key: 'voice_seconds_per_30d', label: 'Голос, секунд за период', unit: 'сек', min: -1 },
  { key: 'max_documents', label: 'Документов базы знаний', unit: '', min: -1 },
  { key: 'max_storage_mb', label: 'Объём хранилища, МБ', unit: 'МБ', min: -1 },
  { key: 'max_file_size_mb', label: 'Размер одного файла, МБ', unit: 'МБ', min: 1 },
];
export const INT_FIELDS = [PERIOD_FIELD, ...LIMIT_FIELDS];

export const num = (v) => (v === '' || v == null ? NaN : Number(v));
export const limitText = (v) => (v === -1 ? 'Безлимит' : v === 0 ? 'Запрещено' : Number(v).toLocaleString('ru-RU'));
export const periodText = (d) => `${d} ${fmt.plural(d, 'день', 'дня', 'дней')}`;
export const seatsText = (n) => `${n} ${fmt.plural(n, 'пространство', 'пространства', 'пространств')}`;
export const featureObj = (rows) => Object.fromEntries(rows.map((r) => [r.key, !!r.on]));
export const sortedJson = (o) => JSON.stringify(Object.entries(o).sort(([a], [b]) => a.localeCompare(b)));

// Только ФЛАГИ (true/false): нефлаговый ключ вроде `expiry_notice_days` (число дней
// или список) переключателем не выразить — `!!on` сделал бы из 14 true. Бэкенд
// такие ключи сохраняет сам, они правятся в Django-админке.
export const featureRowsOf = (features) => Object.entries(features || {})
  .filter(([, on]) => typeof on === 'boolean')
  .map(([key, on]) => ({ key, on }));

/* Общие проверки полей тарифа. Возвращают тексты ошибок по ключам; пустой
   объект — всё в порядке. */
export function validateName(name, { required = true } = {}) {
  const v = String(name || '').trim();
  if (!v) return required ? 'Укажите название тарифа.' : '';
  if (v.length > 120) return 'Не больше 120 символов.';
  return '';
}

export function validatePrice(price) {
  const t = String(price == null ? '' : price).trim();
  if (!t) return 'Укажите цену.';
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return 'Неотрицательное число, не больше двух знаков после запятой.';
  if (Number(t) >= 1e7) return 'Значение слишком велико.';
  return '';
}

export function validateInts(form, fields) {
  const e = {};
  fields.forEach(({ key, min, max }) => {
    const v = num(form[key]);
    if (!Number.isInteger(v)) e[key] = 'Целое число.';
    else if (v < min) e[key] = min === 1 ? 'Не меньше 1 — безлимита у этого поля нет.' : 'Меньше -1 значений не бывает.';
    else if (max && v > max) e[key] = `Не больше ${max}.`;
  });
  return e;
}

export function validateNote(note) {
  return String(note || '').trim().length > 200 ? 'Не больше 200 символов.' : '';
}

/* ── Редактор фич ──
   rows — [{key, on}]; labels — подписи по слагу (каталог сервера поверх
   FEATURE_LABEL); knownKeys — что предложить быстрыми чипами. */
export function FeaturesField({ rows, onChange, error, onError, labels = FEATURE_LABEL, knownKeys = KNOWN_FEATURES, hint }) {
  const [newKey, setNewKey] = useState('');

  const addFeature = (key) => {
    const k = key.trim().toLowerCase();
    if (!FEATURE_KEY_RE.test(k)) { onError('Слаг фичи: латиница в нижнем регистре, цифры и «_».'); return; }
    if (rows.some((r) => r.key === k)) { onError('Такая фича уже в списке.'); return; }
    onChange([...rows, { key: k, on: true }]);
    setNewKey('');
  };

  const missingKnown = knownKeys.filter((k) => !rows.some((r) => r.key === k));

  return (
    <Field label="Фичи тарифа" error={error}
      hint={hint || 'Флаги доступа к разделам продукта. Ключ — слаг, значение — включена или нет.'}>
      <div className="sc-plans-feats">
        {rows.length === 0 && <div className="sc-plans-blank t-caption">Фич нет — тариф даёт только ассистента и лимиты выше.</div>}
        {rows.map((row) => (
          <div className="sc-plans-feat" key={row.key}>
            <span className="sc-plans-feat__name">
              <span className="sc-plans-feat__title">{labels[row.key] || row.key}</span>
              {labels[row.key] && <span className="sc-plans-feat__code num">{row.key}</span>}
            </span>
            <Switch checked={row.on} ariaLabel={`Фича ${row.key}`}
              onChange={(v) => onChange(rows.map((r) => (r.key === row.key ? { ...r, on: v } : r)))} />
            <Button iconOnly="trash" variant="ghost" size="sm" aria-label={`Убрать фичу ${row.key}`}
              onClick={() => onChange(rows.filter((r) => r.key !== row.key))} />
          </div>
        ))}
        {missingKnown.length > 0 && (
          <div className="sc-plans-feat__quick cluster cluster-3">
            {missingKnown.map((k) => (
              <button type="button" key={k} className="sc-plans-chip" onClick={() => addFeature(k)}>
                <Icon name="plus" size={13} />{labels[k] || k}
              </button>
            ))}
          </div>
        )}
        <div className="sc-plans-feat__new">
          <TextInput value={newKey} placeholder="свой слаг фичи, например reports_export"
            autoComplete="off" spellCheck={false}
            onChange={(e) => setNewKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(newKey); } }} />
          <Button variant="secondary" iconLeft="plus" disabled={!newKey.trim()} onClick={() => addFeature(newKey)}>Добавить</Button>
        </div>
      </div>
    </Field>
  );
}
